import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  clearStoredAuth,
  createSession,
  deleteSession,
  fetchMe,
  loginWithSms,
  readStoredAuth,
  writeStoredAuth,
  type AuthUser,
} from '@/services/authApi';

type PendingAction = (() => void) | null;

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  loginOpen: boolean;
  openLogin: () => void;
  closeLogin: () => void;
  /** 打开登录弹窗；成功后执行 pendingAction（续接分析/导出等） */
  requestLogin: (pendingAction?: () => void) => void;
  signInWithSms: (phone: string, code: string) => Promise<void>;
  /** @deprecated 默认走验证码；保留密码兜底 */
  signIn: (phone: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const stored = readStoredAuth();
  const [user, setUser] = useState<AuthUser | null>(stored?.user ?? null);
  const [loading, setLoading] = useState(Boolean(stored?.token));
  const [loginOpen, setLoginOpen] = useState(false);
  const pendingRef = useRef<PendingAction>(null);

  useEffect(() => {
    const current = readStoredAuth();
    if (!current?.token) {
      setLoading(false);
      return;
    }
    let mounted = true;
    fetchMe(current.token, { timeoutMs: 8000 })
      .then((next) => {
        if (!mounted) return;
        if (!next) {
          clearStoredAuth();
          setUser(null);
        } else {
          writeStoredAuth({ token: current.token, user: next });
          setUser(next);
        }
      })
      .catch(() => {
        if (!mounted) return;
        setUser(current.user ?? null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const closeLogin = useCallback(() => {
    setLoginOpen(false);
    // 关闭弹窗不清理 pending 输入态由 LoginModal 负责；放弃续接
    pendingRef.current = null;
  }, []);

  const openLogin = useCallback(() => {
    setLoginOpen(true);
  }, []);

  const requestLogin = useCallback((pendingAction?: () => void) => {
    // 只有「带待办」时才覆盖：无参打开登录框（导航栏登录按钮等）若清空 pending，
    // 会把页面上刚登记的续接动作抹掉，表现为「点了分析 → 登录 → 什么都没发生」。
    if (pendingAction) pendingRef.current = pendingAction;
    setLoginOpen(true);
  }, []);

  const [pendingSeq, setPendingSeq] = useState(0);

  const runPending = useCallback(() => {
    setLoginOpen(false);
    // 只递增序号，真正执行交给下面的 effect：effect 一定在**渲染提交之后**运行，
    // 此时各页面里的 userRef / user state 都已是登录后的新值。
    // 原先用 setTimeout(pending, 0) 与 React 的提交顺序无保证，会出现
    // 「登录成功但续接的动作仍按未登录分支处理」→ 又弹一次登录框、没有任何跳转。
    if (pendingRef.current) setPendingSeq((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!pendingSeq) return;
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    pending();
  }, [pendingSeq]);

  const signInWithSms = useCallback(
    async (phone: string, code: string) => {
      const session = await loginWithSms(phone, code);
      writeStoredAuth(session);
      setUser(session.user);
      runPending();
    },
    [runPending],
  );

  const signIn = useCallback(
    async (phone: string, password: string) => {
      const session = await createSession(phone, password);
      writeStoredAuth(session);
      setUser(session.user);
      runPending();
    },
    [runPending],
  );

  const signOut = useCallback(async () => {
    await deleteSession();
    clearStoredAuth();
    setUser(null);
    pendingRef.current = null;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        loginOpen,
        openLogin,
        closeLogin,
        requestLogin,
        signInWithSms,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook 与 Provider 配对导出
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必须在 AuthProvider 内使用');
  }
  return context;
}
